/** The work queue: one list, many origins.
 *
 * **EXP-24.** A task created from a record and a task that stands alone appear together, because a
 * contributor's day is one list. The review is explicit that *"general tasks may remain
 * unlinked"* — a task to chase a landlord for a lease assessment exists before the record it will
 * eventually hang off does, and that is exactly what makes it worth tracking.
 *
 * **Oldest first**, like the evidence queue and for the same reason (DOC-06): a queue is ordered by
 * how long something has waited, and newest-first hides the item most worth finding. The server
 * decides the order; this screen does not re-sort it unless the reader picks a column.
 *
 * **A table page (ADR-0212, P10).** Four tiles answer "how much is on me and how late is it"; the
 * filter line holds whose work and whether finished work shows; one grid holds the tasks, each with
 * its state as a word and a shape. The tiles are counted from the list the read returned and are not
 * drawn at all until it has: a tile built from `data ?? []` would print `0` over a failed read. The
 * ordering and closing explanation is behind "How the queue works"; closing a task asks for its note
 * in a drawer, before the action and not after a refusal.
 */

import { ListChecks } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import {
  Drawer,
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { ChipTone, FilterState, GridColumn } from "../components/ui";
import { useTasks, useUpdateTask } from "../api/work";
import type { Task } from "../api/work";
import { shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { useGridSort } from "./gridSort";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";
import { NewTask } from "./NewTask";

/** Mirrored from `collaboration.py`'s `MAY_WRITE`. A Viewer and an Auditor read the queue — an
 * engagement is a right to examine what was done — and closing a task is doing the work.
 *
 * **Said rather than discovered.** Before this, "Mark done" was rendered for every role: a Viewer
 * opened the form, typed what was done, pressed the button and collected a 403 — which is UX-04's
 * defect and design rule 6, on the screen somebody starts their day on. */
const MAY_CLOSE: readonly string[] = ["admin", "approver", "contributor"];

const STATE_LABEL: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

/** A state is a word and a shape, never a colour alone (design rule 4). */
const STATE_TONE: Record<string, ChipTone> = {
  open: "info",
  in_progress: "info",
  blocked: "warn",
  done: "ok",
  cancelled: "neutral",
};

const FINISHED: readonly string[] = ["done", "cancelled"];

/** Today as an ISO date - the comparison this screen has always made for "overdue". */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The ISO date `days` from today, for "due this week". */
function inDays(days: number): string {
  const when = new Date();
  when.setUTCDate(when.getUTCDate() + days);
  return when.toISOString().slice(0, 10);
}

function TasksScreenBody() {
  const screen = useScreen();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  //: Mine by default. A queue that opens on everybody's work is a queue a contributor has to
  //: filter before it is useful, and the common case is "what is on me".
  const mine = (filter.values.whose ?? []).length === 0;
  const openOnly = (filter.values.finished ?? []).length === 0;
  //: The query, not `data = []`. A failed read defaulted to an empty array and rendered as
  //: *"Nothing open is assigned to you"* — which is the opposite of the truth when the request for
  //: that list failed (EXP-29). See `Loaded`.
  const queue = useTasks(mine, openOnly ? "open" : undefined);
  const [closing, setClosing] = useState<Task | null>(null);
  const [note, setNote] = useState("");
  /** The refusal, not a sentence. This PATCH always sends `expected_version`, so `stale_version`
   * is its likeliest refusal — and it rendered as one paragraph with no way forward, which is
   * EXP-29's second list exactly: *"a 409 that renders as a generic failure is the defect."* The
   * remedy for a stale decision is to re-read the queue and look, never to re-send. */
  const [refusal, setRefusal] = useState<unknown>(null);
  const update = useUpdateTask();
  const { data: me } = useMe();
  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not close": a queue that
  //: flashed the refusal sentence on every load would teach a contributor to ignore it.
  const mayClose = me ? MAY_CLOSE.includes(me.role) : undefined;

  async function onClose(task: Task) {
    setRefusal(null);
    try {
      await update.mutateAsync({
        taskId: task.id,
        state: "done",
        completionNote: note,
        expectedVersion: task.version,
      });
      setClosing(null);
      setNote("");
    } catch (error) {
      setRefusal(error);
    }
  }

  const today = todayIso();
  const weekEnd = inDays(7);
  //: **Counted among the tasks this read returned, and only once it has.** A tile built from
  //: `data ?? []` would print `0` over a failed read - the same lie as the empty sentence.
  const loaded = queue.data;
  const live = loaded?.filter((task) => !FINISHED.includes(task.state)) ?? [];
  const overdue = live.filter((task) => task.due_date !== null && task.due_date < today);
  const dueSoon = live.filter(
    (task) => task.due_date !== null && task.due_date >= today && task.due_date <= weekEnd,
  );
  const blocked = live.filter((task) => task.state === "blocked");

  const needle = filter.search.trim().toLowerCase();
  const rows = (loaded ?? []).filter(
    (task) =>
      !needle || `${task.title} ${task.completion_criteria ?? ""}`.toLowerCase().includes(needle),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    title: (r) => r.title,
    due: (r) => r.due_date,
    state: (r) => STATE_LABEL[r.state] ?? r.state,
  });

  const columns: GridColumn<Task>[] = [
    {
      id: "title",
      header: "Task",
      sortable: true,
      rowHeader: true,
      cell: (task) => (
        <span title={task.completion_criteria ?? undefined}>
          {task.title}
          {task.completion_note ? ` - closed: ${task.completion_note}` : ""}
        </span>
      ),
    },
    {
      id: "due",
      header: "Due",
      sortable: true,
      cell: (task) => (task.due_date ? shortDate(task.due_date) : "No due date"),
    },
    {
      id: "state",
      header: "State",
      sortable: true,
      cell: (task) => {
        const finished = FINISHED.includes(task.state);
        const late = !finished && task.due_date !== null && task.due_date < today;
        return (
          <span>
            {late ? (
              <StatusChip tone="crit" size="sm">
                Overdue
              </StatusChip>
            ) : null}{" "}
            {task.priority === "high" ? (
              <StatusChip tone="warn" size="sm">
                High
              </StatusChip>
            ) : null}{" "}
            <StatusChip tone={STATE_TONE[task.state] ?? "neutral"} size="sm">
              {STATE_LABEL[task.state] ?? task.state}
            </StatusChip>
          </span>
        );
      },
    },
    {
      id: "record",
      header: "Record",
      cell: (task) =>
        task.record_id ? (
          <Link
            className={styles.fixLink}
            to="/records/$recordId"
            params={{ recordId: task.record_id }}
            search={keepSearch}
            aria-label={`Open the record for: ${task.title}`}
          >
            Open record
          </Link>
        ) : (
          "Not linked"
        ),
    },
    {
      id: "close",
      header: "Close",
      cell: (task) =>
        FINISHED.includes(task.state) || mayClose !== true ? null : (
          <button
            type="button"
            className={styles.secondary}
            onClick={() => {
              setClosing(task);
              setNote("");
              setRefusal(null);
            }}
          >
            Mark done
          </button>
        ),
    },
  ];

  const stopClosing = () => {
    setClosing(null);
    setNote("");
    setRefusal(null);
  };

  return (
    <ScreenFrame screen={screen} title="Tasks & requests" crumb="one queue, many origins">
      <TablePage
        icon={ListChecks}
        title="Action items"
        label="Tasks"
        actions={
          <>
            <NewTask />
            <HowDrawer infoKey="actions.tasks-how" label="How the queue works" />
          </>
        }
        tiles={
          loaded ? (
            <TileStrip label="The work queue at a glance">
              <Tile value={String(live.length)} label="Open" testId="tasks-open" />
              <Tile value={String(overdue.length)} label="Overdue" testId="tasks-overdue" />
              <Tile value={String(dueSoon.length)} label="Due in 7 days" testId="tasks-soon" />
              <Tile value={String(blocked.length)} label="Blocked" testId="tasks-blocked" />
            </TileStrip>
          ) : undefined
        }
        filters={
          <FilterLine
            filters={[
              {
                id: "whose",
                label: "Show",
                mode: "single",
                allLabel: "Mine",
                options: [{ value: "everyone", label: "Everyone's work" }],
              },
              {
                id: "finished",
                label: "Finished",
                mode: "single",
                allLabel: "Hidden",
                options: [{ value: "shown", label: "Shown" }],
              },
            ]}
            state={filter}
            onChange={setFilter}
            resultCount={loaded ? { shown: rows.length, total: loaded.length, noun: "tasks" } : undefined}
          />
        }
        table={
          queue.error ? undefined : (
            <>
              {/* **A control, or the sentence saying who may use it - never neither** (design rule 6,
                  `formOrWhoCan`). Said once for the queue rather than once per row. */}
              {mayClose === false ? (
                <p className={styles.note} data-testid="tasks-who-can">
                  An Admin, an Approver or a Contributor adds or closes a task.
                </p>
              ) : null}
              <GridTable
                caption="Tasks, oldest first"
                columns={columns}
                rows={sorted}
                rowKey={(task) => task.id}
                rowAttributes={(task) => ({ "data-state": task.state })}
                loading={queue.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    compact
                    testId="tasks-empty"
                    title={
                      (loaded?.length ?? 0) > 0
                        ? "No task matches the search."
                        : openOnly
                          ? mine
                            ? "Nothing open is assigned to you."
                            : "Nothing is open in this workspace."
                          : "No tasks yet."
                    }
                  />
                }
                testId="tasks"
              />
            </>
          )
        }
        state={
          queue.error ? (
            <ReadFailed of="The work queue" error={queue.error} onRetry={() => void queue.refetch()} />
          ) : undefined
        }
      />

      {/* Closing needs a note, and the field appears **before** the action rather than after a
          refusal. The server requires it and the database requires it; asking first is the
          difference between a form and a trap. **Cancel restores the prior state**: the drawer
          closed, the note gone and any refusal with it. */}
      <Drawer
        open={closing !== null}
        onClose={stopClosing}
        title="Close this task"
        width="sm"
        testId="task-close-drawer"
        footer={
          closing ? (
            <>
              <button
                type="button"
                className={styles.primary}
                disabled={!note.trim() || update.isPending}
                onClick={() => void onClose(closing)}
              >
                {update.isPending ? "Saving…" : "Close it"}
              </button>
              <button type="button" className={styles.fixLink} onClick={stopClosing}>
                Cancel
              </button>
            </>
          ) : undefined
        }
      >
        {closing ? (
          <>
            <label className={styles.field}>
              <span>What was done?</span>
              <input
                aria-label="What was done?"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="A task closed with no note cannot be checked"
              />
            </label>
            <WriteFailed
              error={refusal}
              of="this queue"
              onReload={() => {
                void queue.refetch();
                stopClosing();
              }}
            />
          </>
        ) : null}
      </Drawer>
    </ScreenFrame>
  );
}

export function TasksScreen() {
  return (
    <RequireSession>
      <TasksScreenBody />
    </RequireSession>
  );
}
